/**
 * @fileoverview Hook de Data Layer for the loading and serialization of customer data in the right sidebar of the inbox.
 * Provides enriched customer data and functions to reload information and order count.
 * @module useInboxRightSidebarData
 * @category Hook - Data Layer
 * @since 1.0.0
 * @version 2.1.0
 */
import {
  useEffect,
  useCallback,
  useMemo,
  useRef
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { visiblePhoneNumber } from '../../../../util/phoneNumber';
import { customerFullName } from '../../../../util/utils';
import {
  getCustomerData,
  getCustomerOrderCount,
  getLeadScore,
  setCustomerDetailsEditing,
  clearCustomerSidebarState
} from '../../../../actions/inbox/customerDataActions';
import ensureInboxCustomer from '../../../../services/inbox/ensureInboxCustomerService';
import { isInboxMsChat } from '../../../../services/inbox/inboxMsChatPlatform';
import useSelectedConversation from '../../shared/useSelectedConversation';

function hasValidCustomerId(customerId) {
  return customerId != null && customerId !== '';
}

/**
 * Hook de Data Layer for the loading and serialization of customer data in the right sidebar of the inbox.
 *
 * Implements the Data Layer pattern with strict separation of responsibilities:
 * - Only manages the loading and serialization of customer and order data
 * - Does not manage UI logic or business validations
 * - Optimized with useCallback and useMemo to avoid unnecessary renders
 *
 * Operation flow:
 * 1. Gets the selected conversation and customer data from Redux
 * 2. Serializes the customer data for the right sidebar
 * 3. Provides functions to reload data and order count
 */
function useInboxRightSidebarData() {
  const dispatch = useDispatch();
  const currentRetailerUser = useSelector(
    (state) => state.mainReducer.currentRetailerUser
  );
  const conversation = useSelectedConversation();
  const conversationRef = useRef(conversation);
  conversationRef.current = conversation;
  const {
    customer,
    customerLoading,
    orderCounts,
    leadScore,
    leadScoreLoading
  } = useSelector((state) => state.inboxRightReducer);
  const retailerInfo = useSelector(
    (state) => state.retailerUsersReducer.retailer_info
  );
  const { platform, id: conversationId, customerId } = conversation || {};

  /**
   * Serializes the customer data for the right sidebar.
   */
  const customerDataSerialized = useCallback((data) => {
    // Visible phone number and formatted
    const phoneNumber = visiblePhoneNumber(data.phone_without_country_code, currentRetailerUser);
    const visiblePhoneNumberValue = visiblePhoneNumber(data.phone, currentRetailerUser);
    const formattedPhone = data.country_id && phoneNumber
      ? `${data.country_id} ${phoneNumber}`.trim()
      : phoneNumber || '';
    // Email and flags
    const email = data.email || '';
    const hasEmail = Boolean(email.trim());
    const hasPhone = Boolean(phoneNumber?.trim());
    // Full name
    const fullName = customerFullName(data, currentRetailerUser);
    return {
      ...data,
      fullName,
      formattedPhone,
      email,
      hasEmail,
      hasPhone,
      platform,
      visiblePhoneNumberValue
    };
  }, [currentRetailerUser, platform]);

  const customerData = useMemo(() => {
    if (!customer) return null;
    return customerDataSerialized(customer);
  }, [customer, customerDataSerialized]);

  const refreshCustomerData = useCallback(() => {
    if (!conversationId) return;
    dispatch(getCustomerData(customerId, { force: true }));
  }, [conversationId, dispatch, customerId]);

  const loadLeadScore = useCallback(async () => {
    if (!conversationId || !platform) return;
    if (!retailerInfo?.ai_copilots_enabled) return;
    dispatch(getLeadScore(customerId, platform));
  }, [conversationId, platform, customerId, retailerInfo?.ai_copilots_enabled, dispatch]);

  useEffect(() => {
    dispatch(setCustomerDetailsEditing(false));
    if (conversationId) {
      dispatch(clearCustomerSidebarState());
    }
  }, [conversationId, dispatch]);

  useEffect(() => {
    if (!conversationId) return;

    if (hasValidCustomerId(customerId)) {
      dispatch(getCustomerData(customerId));
      dispatch(getCustomerOrderCount(customerId));
      loadLeadScore();
      return;
    }

    if (isInboxMsChat(platform)) {
      dispatch(ensureInboxCustomer(conversationRef.current));
    }
  }, [conversationId, customerId, platform, dispatch, loadLeadScore]);

  // Advanced memoization of the return object
  return useMemo(() => ({
    loading: customerLoading,
    orderCounts,
    conversation,
    customerData,
    refreshCustomerData,
    leadScore,
    leadScoreLoading
  }), [
    conversation,
    customerLoading,
    orderCounts,
    customerData,
    refreshCustomerData,
    leadScore,
    leadScoreLoading
  ]);
}

export default useInboxRightSidebarData;
